import { directSupabase } from '../../lib/supabaseClient';
import { documentsService } from '../../modules/docs/services/documents.service';
import { organisationSettings } from '../../services/organisationSettings';

export function normalizeLifecycleRef(value?: string | null) {
  return String(value || '').trim().toUpperCase();
}

/**
 * Mints a workspace ref from the brand's own sequence (KSG-…, KSU-…).
 * Without a brand it uses the default brand from Admin → Organisations.
 */
export async function mintWorkspaceLifecycleRef(organisationId?: string | null) {
  const id = String(organisationId || '').trim() || (await organisationSettings.defaultBrand())?.id;
  if (!id) throw new Error('No organisation is available to assign a lifecycle ref.');
  return documentsService.mintLifecycleRef(id);
}

const DOCUMENT_REF_PRIORITY: Record<string, number> = { Quotation: 0, Estimate: 1, Invoice: 2 };

export function pickLifecycleRefFromDocuments(
  docs: Array<{ kind?: string | null; lifecycle_ref?: string | null; created_at?: string | null }>,
): string {
  const ranked = docs
    .filter((doc) => normalizeLifecycleRef(doc.lifecycle_ref))
    .sort((a, b) => {
      const byKind = (DOCUMENT_REF_PRIORITY[String(a.kind)] ?? 9) - (DOCUMENT_REF_PRIORITY[String(b.kind)] ?? 9);
      if (byKind !== 0) return byKind;
      return String(a.created_at || '').localeCompare(String(b.created_at || ''));
    });
  return ranked.length ? normalizeLifecycleRef(ranked[0].lifecycle_ref) : '';
}

async function lifecycleRefFromProjectDocuments(projectId: string): Promise<string> {
  const { data, error } = await directSupabase
    .from('documents')
    .select('kind, lifecycle_ref, created_at')
    .eq('project_id', projectId);
  if (error) throw new Error(error.message);
  return pickLifecycleRefFromDocuments(data || []);
}

/**
 * Workspace ref: keep the stored one, else adopt the ref its documents already carry,
 * else mint (unless `mint: false`). Documents may have been sent to the customer under their ref.
 */
export async function ensureProjectLifecycleRef(
  projectId: string,
  current?: string | null,
  options: { mint?: boolean } = {},
) {
  const existing = normalizeLifecycleRef(current);
  if (existing) return existing;

  const { data: row, error: readError } = await directSupabase
    .from('projects')
    .select('lifecycle_ref')
    .eq('id', projectId)
    .maybeSingle();
  if (readError) throw new Error(readError.message);
  const stored = normalizeLifecycleRef(row?.lifecycle_ref);
  if (stored) return stored;

  const fromDocuments = await lifecycleRefFromProjectDocuments(projectId);
  if (!fromDocuments && options.mint === false) return '';
  const assigned = fromDocuments || await mintWorkspaceLifecycleRef();
  const { data: updated, error: writeError } = await directSupabase
    .from('projects')
    .update({ lifecycle_ref: assigned })
    .eq('id', projectId)
    .is('lifecycle_ref', null)
    .select('lifecycle_ref')
    .maybeSingle();
  if (writeError) throw new Error(writeError.message);
  if (updated?.lifecycle_ref) return normalizeLifecycleRef(updated.lifecycle_ref);

  const { data: raced, error: racedError } = await directSupabase
    .from('projects')
    .select('lifecycle_ref')
    .eq('id', projectId)
    .maybeSingle();
  if (racedError) throw new Error(racedError.message);
  return normalizeLifecycleRef(raced?.lifecycle_ref) || assigned;
}

export async function updateProjectLifecycleRef(projectId: string, previous: string | null | undefined, next: string) {
  const normalized = normalizeLifecycleRef(next);
  if (!normalized) throw new Error('Enter a lifecycle ref.');
  const previousNormalized = normalizeLifecycleRef(previous);
  if (normalized === previousNormalized) return normalized;

  const { data: clash, error: clashError } = await directSupabase
    .from('projects')
    .select('id')
    .eq('lifecycle_ref', normalized)
    .neq('id', projectId)
    .limit(1);
  if (clashError) throw new Error(clashError.message);
  if (clash && clash.length > 0) {
    throw new Error(`${normalized} is already used by another workspace.`);
  }

  const { data: docClash, error: docClashError } = await directSupabase
    .from('documents')
    .select('id')
    .eq('lifecycle_ref', normalized)
    .neq('project_id', projectId)
    .limit(1);
  if (docClashError) throw new Error(docClashError.message);
  if (docClash && docClash.length > 0) {
    throw new Error(`${normalized} is already used on another document.`);
  }

  // Documents outside any workspace (project_id is null) are missed by the check above
  const { data: looseClash, error: looseClashError } = await directSupabase
    .from('documents')
    .select('id')
    .eq('lifecycle_ref', normalized)
    .is('project_id', null)
    .limit(1);
  if (looseClashError) throw new Error(looseClashError.message);
  if (looseClash && looseClash.length > 0) {
    throw new Error(`${normalized} is already used on another document.`);
  }

  // Invoicing arrangements share the KSU sequence with KSU workspaces
  const { data: arrangementClash, error: arrangementClashError } = await (directSupabase as any)
    .from('billing_arrangements')
    .select('id')
    .eq('lifecycle_ref', normalized)
    .limit(1);
  if (arrangementClashError && !/billing_arrangements|relation/i.test(arrangementClashError.message)) {
    throw new Error(arrangementClashError.message);
  }
  if (arrangementClash && arrangementClash.length > 0) {
    throw new Error(`${normalized} is already used by an Invoicing arrangement.`);
  }

  const { error: projectError } = await directSupabase
    .from('projects')
    .update({ lifecycle_ref: normalized })
    .eq('id', projectId);
  if (projectError) throw new Error(projectError.message);

  if (previousNormalized) {
    const { error: docError } = await directSupabase
      .from('documents')
      .update({ lifecycle_ref: normalized, ref_no: normalized })
      .eq('project_id', projectId)
      .eq('lifecycle_ref', previousNormalized);
    if (docError) throw new Error(docError.message);
  }

  return normalized;
}
